/**
 * UserYjsDocManager - Manages Yjs documents for User CRDT sync
 *
 * Handles the user's vault index (folders, docs, keypair)
 * and preferences (calendarConfig) using CRDT for multi-device sync.
 *
 * Schema uses Y.Map with IDs as keys (not Y.Array) to prevent duplicates
 * and enable proper conflict resolution.
 *
 * Structure:
 *   vaultIndex: Y.Map {
 *     folders: Y.Map<folderId, FolderEntry>
 *     docs:    Y.Map<docId, DocEntry>
 *     keypair: Y.Map { publicKey, secretKey, serverSynced }
 *   }
 *   preferences: Y.Map {
 *     calendarConfig: { ... }
 *   }
 *   metadata: Y.Map {
 *     schema: "orbital.user.v1"
 *     createdAt: timestamp
 *     updatedAt: timestamp
 *   }
 */

import * as Y from "yjs";
import { createUserPersistence } from "../storage/index.js";
import * as StorageHealth from "./storage-health.js";
import { ensureVaultStructure, assertCompatibleVaultStructure } from "./vault-structure.js";
import { markChanged } from "./unsynced-changes.js";

/**
 * UserYjsDocManager class
 * Manages the User CRDT document for vault index and preferences
 */
export class UserYjsDocManager {
  constructor(userId, options = {}) {
    this.userId = userId;
    this.docId = `user-${userId}`;
    this.doc = new Y.Doc();

    // Top-level Y.Maps
    this.vaultIndex = this.doc.getMap("vaultIndex");
    this.preferences = this.doc.getMap("preferences");
    this.metadata = this.doc.getMap("metadata");

    this.persistence = null;
    this._stopPersistenceMonitor = null;
    this.onUpdate = options.onUpdate || null;

    // Track if we're currently making local changes
    this._isLocalChange = false;

    // A change made here: not on the server until it confirms it
    // (unsynced-changes.js; sign-out names what it would lose).
    this.doc.on("update", (update, origin) => {
      if (origin === "local") markChanged(this.docId);
    });

    // NOTE: We do NOT call _initVaultIndexStructure() here anymore.
    // It must be called AFTER sync to avoid CRDT conflicts where the
    // client's empty Y.Maps override the server's populated Y.Maps.
  }

  /**
   * Initialize the nested Y.Map structure for vaultIndex
   * This ensures the structure exists even for new documents
   *
   * IMPORTANT: Uses origin "local" so the structure creation is synced to server.
   * Without this, setDocEntry() would set values on Y.Maps that don't exist on the server,
   * and the deltas would be meaningless when applied server-side.
   *
   */
  _initVaultIndexStructure() {
    ensureVaultStructure(this.doc);
  }

  /**
   * Get the nested Y.Maps
   */
  get folders() {
    return this.vaultIndex.get("folders");
  }

  get docs() {
    return this.vaultIndex.get("docs");
  }

  get keypair() {
    return this.vaultIndex.get("keypair");
  }

  get calendarConfig() {
    return this.preferences.get("calendarConfig");
  }

  /**
   * Initialize IndexedDB persistence for offline support
   */
  async initPersistence() {
    if (this.persistence) return;

    this.persistence = createUserPersistence(this.userId, this.doc);

    await StorageHealth.waitForIndexedDBSync(this.persistence, `orbital-user-${this.userId}`);
    console.log(`[UserYjsDoc] Loaded from IndexedDB: ${this.docId}`);
    this._stopPersistenceMonitor = StorageHealth.monitorIndexedDBPersistence(
      this.persistence,
      `orbital-user-${this.userId}`,
    );
    // NOTE: We do NOT call _initVaultIndexStructure() here.
    // It must be called AFTER WebSocket sync to avoid CRDT conflicts.
  }

  /**
   * Check if the document has any data
   * @returns {boolean} True if folders or docs have entries
   */
  hasData() {
    const folders = this.folders;
    const docs = this.docs;
    return !!((folders && folders.size > 0) ||
              (docs && docs.size > 0));
  }

  // ============================================
  // Folder Operations
  // ============================================

  /**
   * Add or update a folder
   * @param {Object} folder - { id, parentId, name, createdAt, updatedAt }
   */
  setFolder(folder) {
    this._isLocalChange = true;
    try {
      this.doc.transact(() => {
        this.folders.set(folder.id, {
          ...folder,
          updatedAt: Date.now(),
        });
      }, "local");
    } finally {
      this._isLocalChange = false;
    }
  }

  /**
   * Get a folder by ID
   * @param {string} folderId
   * @returns {Object|null}
   */
  getFolder(folderId) {
    const entry = this.folders.get(folderId);
    if (!entry) return null;
    // entry might be a Y.Map, convert to plain object
    return typeof entry.toJSON === "function" ? entry.toJSON() : entry;
  }

  /**
   * Get all folders as an array
   * @returns {Array}
   */
  getAllFolders() {
    const result = [];
    const folders = this.folders;
    if (!folders) {
      console.warn("[UserYjsDoc] getAllFolders: vaultIndex.folders is undefined (CRDT not initialized)");
      return result;
    }
    folders.forEach((entry, id) => {
      // entry might be a Y.Map, convert to plain object
      const folder = typeof entry.toJSON === "function" ? entry.toJSON() : entry;
      result.push({ ...folder, id });
    });
    return result;
  }

  /**
   * Delete a folder
   * @param {string} folderId
   */
  deleteFolder(folderId) {
    this._isLocalChange = true;
    try {
      this.doc.transact(() => {
        this.folders.delete(folderId);
      }, "local");
    } finally {
      this._isLocalChange = false;
    }
  }

  /**
   * Check if the unified docs migration has been completed.
   * @returns {boolean}
   */
  hasUnifiedDocsMigration() {
    const metadata = this.vaultIndex.get("metadata");
    if (metadata instanceof Y.Map) {
      return metadata.get("unifiedDocsMigration") === true;
    }
    return false;
  }

  /**
   * Migrate vault index from maps[] + sharedSubtrees[] → docs[].
   * Called once on login if docs[] doesn't exist/is empty but maps[] or sharedSubtrees[] do.
   * Uploads keypair-wrapped DEKs for personal docs via PUT /api/docs/:id/keys/me.
   *
   * @param {CryptoKey} masterKey - For unwrapping KEK-based DEKs
   * @param {Object} keypair - { publicKey, secretKey } in Base64
   * @returns {Promise<{ migratedDocs: number, uploadedKeys: number }>}
   */
  async migrateToUnifiedDocs(masterKey, keypair) {
    if (this.hasUnifiedDocsMigration()) {
      return { migratedDocs: 0, uploadedKeys: 0 };
    }

    // Read legacy Y.Maps directly (they may still exist in old CRDT documents)
    const maps = this.vaultIndex.get("maps");
    const sharedSubtrees = this.vaultIndex.get("sharedSubtrees");

    if ((!maps || maps.size === 0) && (!sharedSubtrees || sharedSubtrees.size === 0)) {
      this._markUnifiedDocsMigrationDone();
      return { migratedDocs: 0, uploadedKeys: 0 };
    }

    const { Keypair } = await import("../crypto/keypair.js");
    const { Vault } = await import("../crypto/vault.js");
    const ownPubKey = Keypair.fromBase64(keypair.publicKey);

    let migratedDocs = 0;
    let uploadedKeys = 0;
    const entries = [];

    // 1. Process personal maps (maps[]) — need KEK unwrap + keypair re-wrap
    if (maps) {
      for (const [docId, mapRaw] of maps.entries()) {
        const mapEntry = typeof mapRaw.toJSON === "function" ? mapRaw.toJSON() : { ...mapRaw };

        let wrappedDEK = null;
        try {
          const dekBytes = await Vault.unwrapKey(masterKey, mapEntry.wrappedDEK, docId);
          const rewrapped = await Keypair.wrapDEKAnonymous(dekBytes, ownPubKey);
          wrappedDEK = Keypair.toBase64(rewrapped);

          const uploadResp = await fetch(`/api/docs/${docId}/keys/me`, {
            method: "PUT",
            credentials: "include",
            headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
            body: JSON.stringify({ wrapped_dek: wrappedDEK }),
          });
          if (!uploadResp.ok) {
            console.warn(`[Migration] DEK upload failed for doc ${docId}: HTTP ${uploadResp.status}`);
          } else {
            uploadedKeys++;
          }
        } catch (e) {
          console.error(`[Migration] Failed to re-wrap DEK for doc ${docId}:`, e);
          // Continue with null wrappedDEK; server still allows access via doc_keys row
        }

        entries.push({
          docId,
          title: mapEntry.title,
          folderId: mapEntry.folderId,
          role: "owner",
          wrappedDEK,
          createdAt: mapEntry.createdAt,
          updatedAt: mapEntry.updatedAt,
          meta: mapEntry.meta || { pinned: false, color: null },
        });
        migratedDocs++;
      }
    }

    // 2. Process shared subtrees (sharedSubtrees[]) — already keypair-wrapped
    if (sharedSubtrees) {
      for (const [shareId, shareRaw] of sharedSubtrees.entries()) {
        const share = typeof shareRaw.toJSON === "function" ? shareRaw.toJSON() : { ...shareRaw };

        entries.push({
          docId: shareId,
          title: share.title,
          folderId: null,
          role: share.role || "viewer",
          wrappedDEK: share.wrappedDEK,
          ownerUsername: share.ownerUsername,
          createdAt: share.addedAt || Date.now(),
          updatedAt: share.updatedAt || Date.now(),
          meta: { pinned: false, color: null },
          ...(share.isOwner ? { isOwnedShare: true, sourceDocId: share.sourceDocId } : {}),
        });
        migratedDocs++;
      }
    }

    // 3. Write to docs[] in CRDT transaction
    this._isLocalChange = true;
    try {
      this.doc.transact(() => {
        ensureVaultStructure(this.doc);
        const docsMap = this.vaultIndex.get("docs");
        for (const entry of entries) {
          docsMap.set(entry.docId, entry);
        }

        ensureVaultStructure(this.doc);
        this.vaultIndex.get("metadata").set("unifiedDocsMigration", true);
        this.vaultIndex.get("metadata").set("unifiedDocsMigratedAt", Date.now());
      }, "local");
    } finally {
      this._isLocalChange = false;
    }

    console.log(`[Migration] Unified docs: ${migratedDocs} docs migrated, ${uploadedKeys} DEKs uploaded`);
    return { migratedDocs, uploadedKeys };
  }

  _markUnifiedDocsMigrationDone() {
    this._isLocalChange = true;
    try {
      this.doc.transact(() => {
        ensureVaultStructure(this.doc);
        this.vaultIndex.get("metadata").set("unifiedDocsMigration", true);
      }, "local");
    } finally {
      this._isLocalChange = false;
    }
  }

  // ============================================
  // Doc Entry Operations
  // ============================================

  /**
   * Add or update a doc entry
   * @param {Object} docEntry - { docId, title, folderId, role, wrappedDEK, ... }
   */
  setDocEntry(docEntry) {
    this._isLocalChange = true;
    try {
      this.doc.transact(() => {
        // Keep verified key generations outside the whole-entry LWW register.
        // A concurrent rename from an offline device must not roll back trust
        // or discard the only wrapping of a new key. Never delete this journal
        // when removing a vault entry: old keys may still be needed for recovery.
        if (docEntry.keyAuthority && docEntry.wrappedDEK) {
          const state = docEntry.keyAuthority;
          this.doc.getMap('mapKeyTrust').set(`${docEntry.docId}:${state.id}`, {
            docId: docEntry.docId, state, wrappedDEK: docEntry.wrappedDEK,
            previousWrappedDEKs: docEntry.previousWrappedDEKs || [],
          });
        }
        this.docs.set(docEntry.docId, { ...docEntry, updatedAt: Date.now() });
      }, "local");
    } finally {
      this._isLocalChange = false;
    }
  }

  /**
   * Key of a map stored on this device, for its attachments (base64). Kept
   * apart from the map's entry, which is written whole (last write wins), so
   * a rename from an older copy of the entry can never drop it; never
   * removed, since files encrypted with it may outlive the entry.
   */
  getLocalMapKey(docId) {
    return this.doc.getMap('localMapKeys').get(docId) || null;
  }

  /** Store a map's key unless it already has one; returns the key in force. */
  setLocalMapKey(docId, keyBase64) {
    const keys = this.doc.getMap('localMapKeys');
    const existing = keys.get(docId);
    if (existing) return existing;
    this._isLocalChange = true;
    try {
      this.doc.transact(() => keys.set(docId, keyBase64), "local");
    } finally {
      this._isLocalChange = false;
    }
    return keyBase64;
  }

  /**
   * Get a doc entry by docId
   * @param {string} docId
   * @returns {Object|null}
   */
  getDocEntry(docId) {
    const entry = this.docs?.get(docId);
    if (!entry) return null;
    return this._withKeyTrust(typeof entry.toJSON === "function" ? entry.toJSON() : entry);
  }

  _withKeyTrust(entry) {
    let latest = null;
    let conflict = false;
    const generations = new Map();
    const wraps = new Set(entry.previousWrappedDEKs || []);
    if (entry.wrappedDEK) wraps.add(entry.wrappedDEK);
    this.doc.getMap('mapKeyTrust').forEach(record => {
      if (record.docId !== entry.docId) return;
      if (generations.has(record.state.epoch) && generations.get(record.state.epoch) !== record.state.id) conflict = true;
      generations.set(record.state.epoch, record.state.id);
      wraps.add(record.wrappedDEK);
      for (const wrap of record.previousWrappedDEKs || []) wraps.add(wrap);
      if (!latest || record.state.epoch > latest.state.epoch) latest = record;
    });
    if (!latest) return entry;
    // Competing authenticated branches need explicit reconciliation. Keep both
    // records and reject key use instead of arbitrarily choosing a winner.
    wraps.delete(latest.wrappedDEK);
    return { ...entry, keyAuthority: latest.state, wrappedDEK: latest.wrappedDEK,
      previousWrappedDEKs: [...wraps], ...(conflict && { _keyAuthorityConflict: true }) };
  }

  /**
   * Get all doc entries as an array
   * @returns {Array}
   */
  getAllDocEntries() {
    const result = [];
    const docs = this.docs;
    if (!docs) {
      console.warn("[UserYjsDoc] getAllDocEntries: vaultIndex.docs is undefined (CRDT not initialized)");
      return result;
    }
    docs.forEach((entry, docId) => {
      const doc = typeof entry.toJSON === "function" ? entry.toJSON() : entry;
      result.push(this._withKeyTrust({ ...doc, docId }));
    });
    return result;
  }

  /**
   * Delete a doc entry
   * @param {string} docId
   */
  deleteDocEntry(docId) {
    this._isLocalChange = true;
    try {
      this.doc.transact(() => {
        this.docs.delete(docId);
      }, "local");
    } finally {
      this._isLocalChange = false;
    }
  }

  // ============================================
  // Keypair Operations
  // ============================================

  /**
   * Set the user's keypair
   * @param {Object} kp - { publicKey, secretKey, serverSynced }
   */
  setKeypair(kp) {
    if (!this.keypair) {
      console.warn("[UserYjsDoc] setKeypair: vault structure not initialized, skipping");
      return;
    }
    this._isLocalChange = true;
    try {
      this.doc.transact(() => {
        this.keypair.set("publicKey", kp.publicKey);
        this.keypair.set("secretKey", kp.secretKey);
        this.keypair.set("serverSynced", kp.serverSynced || false);
        this.keypair.set("updatedAt", Date.now());
      }, "local");
    } finally {
      this._isLocalChange = false;
    }
  }

  /**
   * Get the user's keypair
   * @returns {Object|null} { publicKey, secretKey, serverSynced }
   */
  getKeypair() {
    const kpMap = this.keypair;
    if (!kpMap) return null;
    const publicKey = kpMap.get("publicKey");
    const secretKey = kpMap.get("secretKey");
    if (!publicKey || !secretKey) return null;
    return {
      publicKey,
      secretKey,
      serverSynced: kpMap.get("serverSynced") || false,
    };
  }

  // ============================================
  // Calendar Config Operations
  // ============================================

  /**
   * Set calendar configuration
   * @param {Object} config - Calendar config object
   */
  setCalendarConfig(config) {
    this._isLocalChange = true;
    try {
      this.doc.transact(() => {
        const calConfig = this.calendarConfig;
        const newKeys = new Set(Object.keys(config));

        // Delete keys that are no longer in the new config
        const existingKeys = [];
        calConfig.forEach((_, key) => existingKeys.push(key));
        for (const key of existingKeys) {
          if (key !== "updatedAt" && !newKeys.has(key)) {
            calConfig.delete(key);
          }
        }

        // Only set keys whose values have changed
        Object.entries(config).forEach(([key, value]) => {
          if (calConfig.get(key) !== value) {
            calConfig.set(key, value);
          }
        });
        calConfig.set("updatedAt", Date.now());
      }, "local");
    } finally {
      this._isLocalChange = false;
    }
  }

  /**
   * Get calendar configuration
   * @returns {Object}
   */
  getCalendarConfig() {
    const config = {};
    this.calendarConfig.forEach((value, key) => {
      config[key] = value;
    });
    return config;
  }

  // ============================================
  // Metadata Operations
  // ============================================

  /**
   * Set metadata value
   * @param {string} key
   * @param {*} value
   */
  setMetadata(key, value) {
    this.doc.transact(() => {
      this.metadata.set(key, value);
    }, "local");
  }

  /**
   * Get metadata value
   * @param {string} key
   * @returns {*}
   */
  getMetadata(key) {
    return this.metadata.get(key);
  }

  // ============================================
  // Full Index Operations (for migration)
  // ============================================

  /**
   * Load a full vault index from the legacy format
   * Used for migrating from the old records-based storage
   * @param {Object} index - Legacy index { folders, maps, sharedSubtrees, ... }
   */
  fromLegacyIndex(index) {
    console.log("[UserYjsDoc] Loading from legacy index");

    // Ensure structure exists before loading data
    this._initVaultIndexStructure("legacy-migration-server-confirmed");

    this._isLocalChange = true;
    try {
      this.doc.transact(() => {
        // NOTE: We intentionally do NOT clear existing data here.
        // This function is additive-only to prevent data loss in CRDT systems.
        // If called with empty index when server returns no data (sync issue),
        // we don't want to delete existing local/synced data.

        // Load folders (additive - only adds/updates, doesn't delete)
        if (index.folders) {
          index.folders.forEach((folder) => {
            this.folders.set(folder.id, folder);
          });
        }

        // Load maps into unified docs (additive)
        if (index.maps) {
          index.maps.forEach((map) => {
            this.docs.set(map.docId, {
              docId: map.docId,
              title: map.title,
              folderId: map.folderId,
              role: "owner",
              wrappedDEK: map.wrappedDEK,
              createdAt: map.createdAt,
              updatedAt: map.updatedAt,
              meta: map.meta || { pinned: false, color: null },
            });
          });
        }

        // Migrate legacy sharedSubtrees[] entries into unified docs (additive)
        if (index.sharedSubtrees) {
          index.sharedSubtrees.forEach((share) => {
            this.docs.set(share.shareId, {
              docId: share.shareId,
              title: share.title,
              folderId: null,
              role: share.role || "viewer",
              wrappedDEK: share.wrappedDEK,
              ownerUsername: share.ownerUsername,
              createdAt: share.addedAt || Date.now(),
              updatedAt: share.updatedAt || Date.now(),
              meta: { pinned: false, color: null },
            });
          });
        }

        // Load keypair if present (overwrites existing - intentional for migration)
        if (index.keypair) {
          this.keypair.set("publicKey", index.keypair.publicKey);
          this.keypair.set("secretKey", index.keypair.secretKey);
          this.keypair.set("serverSynced", index.keypair.serverSynced || false);
          this.keypair.set("updatedAt", Date.now());
        }

        // Set metadata
        this.metadata.set("schema", "orbital.user.v1");
        this.metadata.set("createdAt", index.createdAt || Date.now());
        this.metadata.set("updatedAt", Date.now());
        this.metadata.set("migratedFrom", "legacy");
      }, "local");
      console.log(
        "[UserYjsDoc] Loaded:",
        this.folders.size,
        "folders,",
        this.docs.size,
        "docs",
      );
    } finally {
      this._isLocalChange = false;
    }
  }

  /**
   * Export to legacy index format
   * Used for backward compatibility and debugging
   * @returns {Object} Legacy-format index
   */
  toLegacyIndex() {
    return {
      schema: this.metadata.get("schema") || "orbital.user.v1",
      createdAt: this.metadata.get("createdAt") || Date.now(),
      updatedAt: this.metadata.get("updatedAt") || Date.now(),
      folders: this.getAllFolders(),
      docs: this.getAllDocEntries(),
      keypair: this.getKeypair(),
    };
  }

  // ============================================
  // Sync Operations
  // ============================================

  /**
   * Get the Yjs state vector for sync
   * @returns {Uint8Array}
   */
  getStateVector() {
    return Y.encodeStateVector(this.doc);
  }

  /**
   * Get updates since a given state vector
   * @param {Uint8Array} stateVector
   * @returns {Uint8Array}
   */
  getUpdatesSince(stateVector) {
    return Y.encodeStateAsUpdate(this.doc, stateVector);
  }

  /**
   * Apply updates from server
   * @param {Uint8Array} update
   */
  applyUpdate(update) {
    assertCompatibleVaultStructure(this.doc, update);
    Y.applyUpdate(this.doc, update, "remote");

    // Notify the app of remote changes
    if (this.onUpdate) {
      this.onUpdate();
    }
  }

  /** Detect saved offline edits, including deletions, after a full server read.
   * State vectors alone cannot detect deletes made before this session started.
   */
  hasUpdatesNotIn(serverUpdates) {
    const server = new Y.Doc();
    try {
      for (const update of serverUpdates) Y.applyUpdate(server, update);
      const remote = Y.encodeStateAsUpdate(server);
      const local = this.getFullState();
      return local.length !== remote.length || local.some((byte, i) => byte !== remote[i]);
    } finally { server.destroy(); }
  }

  /**
   * Get full document state for initial sync
   * @returns {Uint8Array}
   */
  getFullState() {
    return Y.encodeStateAsUpdate(this.doc);
  }

  /**
   * Subscribe to document updates (for sending to server)
   * @param {Function} callback - Called with update info
   * @returns {Function} Unsubscribe function
   */
  onDocUpdate(callback) {
    const handler = (update, origin) => {
      // Only send updates for local changes
      const isLocalChange = origin === "local";
      if (isLocalChange) {
        callback({ type: "delta", data: update });
      }
    };

    this.doc.on("update", handler);
    return () => this.doc.off("update", handler);
  }

  /**
   * Get full state for compaction
   * @param {string} reason
   * @returns {Object} Update info with type 'fullState', data, stateVector, reason
   */
  getFullStateForCompaction(reason = "periodic") {
    return {
      type: "fullState",
      data: this.getFullState(),
      stateVector: this.getStateVector(), // Plaintext, sent alongside encrypted data
      reason,
    };
  }

  /**
   * Clean up resources
   */
  async destroy() {
    if (this.persistence) {
      await this.persistence.destroy();
      this._stopPersistenceMonitor?.();
      this._stopPersistenceMonitor = null;
    }
    this.doc.destroy();
  }
}
